import { Link, Typography } from '@omnipresentgroup/design-system';
import {
  CountriesPurpose,
  useSupportedCountries,
} from '@omnipresentgroup/domains';
import {
  getCurrencyInputStep,
  toSentenceCase,
} from '@omnipresentgroup/helpers';
import {
  BillingCurrencyEnum,
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
} from '@omnipresentgroup/types';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { useEffect, useMemo, useState } from 'react';

import type { AddContractorFormFields } from '../types';

const SUPPORT_ARTICLE_LINK =
  'https://help.omnipresent.com/en/articles/9184678-bank-charges-when-sending-usd';

const getArraySchemaFromEnum = (
  enumObject: object,
  transformTitle = true,
): JSONSchema7Definition[] =>
  Object.values(enumObject).map((type) => ({
    const: type,
    title: transformTitle ? toSentenceCase(type) : type,
  }));

export const useAddContractorSchema = ({
  formValues,
}: {
  formValues?: AddContractorFormFields;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  const { data: countries, isLoading: isLoadingCountries } =
    useSupportedCountries({
      purpose: CountriesPurpose.RESIDENCE,
    });

  const startDate = useMemo(
    () =>
      formValues?.contractualAgreement?.contractualAgreementDateRange
        ?.contractualAgreementStartDate,
    [
      formValues?.contractualAgreement?.contractualAgreementDateRange
        ?.contractualAgreementStartDate,
    ],
  );

  const endDate = useMemo(
    () =>
      formValues?.contractualAgreement?.contractualAgreementDateRange
        ?.contractualAgreementEndDate,
    [
      formValues?.contractualAgreement?.contractualAgreementDateRange
        ?.contractualAgreementEndDate,
    ],
  );

  const currency = useMemo(
    () => formValues?.contractualAgreement?.invoicingAmount?.currency,
    [formValues?.contractualAgreement?.invoicingAmount?.currency],
  );

  const isSchemaReady = useMemo(
    () => !isLoadingCountries && !!countries,
    [countries, isLoadingCountries],
  );

  useEffect(() => {
    isSchemaReady &&
      setSchema({
        type: 'object',
        required: [
          'contractorDetails',
          'contractualAgreement',
          'billingArrangements',
        ],
        properties: {
          contractorDetails: {
            type: 'object',
            title: 'Contractor details',
            properties: {
              contractorsFirstName: {
                title: 'First name',
                type: 'string',
              },
              contractorsLastName: {
                title: 'Last name',
                type: 'string',
              },
              contractorsRole: {
                title: 'Role',
                type: 'string',
              },
              contractorsEmail: {
                title: 'Email address',
                type: 'string',
                format: 'email',
              },
              contractorsCountry: {
                title: 'Country',
                type: 'string',
                oneOf: countries?.map((country) => ({
                  const: country.code,
                  title: country.name,
                })),
              },
            },
            required: [
              'contractorsFirstName',
              'contractorsLastName',
              'contractorsRole',
              'contractorsEmail',
              'contractorsCountry',
            ],
          },
          contractualAgreement: {
            type: 'object',
            properties: {
              invoicingAmount: {
                type: 'object',
                required: ['currency'],
                properties: {
                  currency: {
                    type: 'string',
                    title: 'Currency',
                    oneOf: getArraySchemaFromEnum(InvoicingCurrencyEnum, false),
                  },
                  amount: {
                    type: 'number',
                    title: 'Amount',
                    minimum: 0.01,
                  },
                },
              },
              contractualAgreementFrequency: {
                title: 'Frequency',
                type: 'string',
                oneOf: getArraySchemaFromEnum(InvoicingFrequencyEnum),
              },
              contractualAgreementDateRange: {
                type: 'object',
                properties: {
                  contractualAgreementStartDate: {
                    title: 'Start date',
                    type: 'string',
                    format: 'date',
                  },
                  contractualAgreementEndDate: {
                    title: 'End date',
                    type: 'string',
                    format: 'date',
                  },
                },
              },
            },
            required: ['invoicingAmount'],
            title: 'Contractual agreement',
          },
          billingArrangements: {
            type: 'object',
            properties: {
              billingArrangementsCurrency: {
                title:
                  'In what currency do you wish to pay for this contractor?',
                type: 'string',
                oneOf: getArraySchemaFromEnum(BillingCurrencyEnum, false),
              },
            },
            required: ['billingArrangementsCurrency'],
            title: 'Billing arrangements',
          },
        },
      });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': [
        'contractorDetails',
        'contractualAgreement',
        'billingArrangements',
      ],
      contractorDetails: {
        'ui:options': {
          isSection: true,
        },
        'ui:order': [
          'contractorsFirstName',
          'contractorsLastName',
          'contractorsRole',
          'contractorsEmail',
          'contractorsCountry',
        ],
        contractorsFirstName: {},
        contractorsLastName: {},
        contractorsRole: {},
        contractorsEmail: {},
        contractorsCountry: {
          'ui:placeholder': 'Select a country...',
          'ui:tooltip': "This is your contractor's fiscal country of residence",
          'ui:selectIcons': countries?.map((country) => ({
            const: country.code,
            icon: country.flag,
          })),
          'ui:options': {
            isSearchable: true,
          },
        },
      },
      contractualAgreement: {
        'ui:options': {
          isSection: true,
        },
        'ui:order': [
          'invoicingAmount',
          'contractualAgreementFrequency',
          'contractualAgreementDateRange',
        ],
        invoicingAmount: {
          'ui:label': false,
          'ui:columns': { columns: 12, gap: '16' },
          currency: {
            'ui:span': 4,
            'ui:placeholder': 'Select',
            'ui:tooltip':
              'This is the currency in which your contractor will issue invocies, and receive payment in their bank account.',
          },
          amount: {
            'ui:span': 8,
            'ui:placeholder': '0.00',
            'ui:options': {
              step: getCurrencyInputStep(currency),
            },
            'ui:tooltip':
              'This is the agreed payment between you and the contractor, and is only intended to help you keep track of your agreement in the platform. This may or may not be different to the amount/frequency your contractor invoices you.',
          },
        },
        contractualAgreementFrequency: {
          'ui:placeholder': 'Select',
        },
        contractualAgreementDateRange: {
          'ui:order': [
            'contractualAgreementStartDate',
            'contractualAgreementEndDate',
          ],
          'ui:label': false,
          'ui:columns': { columns: 12, gap: '16' },
          contractualAgreementStartDate: {
            'ui:span': 6,
            'ui:options': {
              selectsStart: true,
              startDate: startDate,
              endDate: endDate,
              maxDate: endDate,
            },
          },
          contractualAgreementEndDate: {
            'ui:span': 6,
            'ui:options': {
              selectsEnd: true,
              startDate: startDate,
              endDate: endDate,
              minDate: startDate,
              filterDate: {
                name: 'exclude-past',
                ref: {
                  count: -1,
                  unit: 'day',
                },
              },
            },
          },
        },
      },
      billingArrangements: {
        'ui:options': {
          isSection: true,
        },
        'ui:order': ['billingArrangementsCurrency'],
        billingArrangementsCurrency: {
          'ui:widget': 'radio',
          'ui:tooltip':
            'This is the currency in which your invoices will be paid by you.',
          'ui:description': (
            <Typography as="span" size="14" color="subtle">
              Please keep in mind that US bank accounts might charge $30-$50 for
              international wire transfer fee. Check out{' '}
              <Link size="14" to={SUPPORT_ARTICLE_LINK} target="_blank">
                more
              </Link>
              .
            </Typography>
          ),
        },
      },
    });
  }, [countries, currency, endDate, startDate, isSchemaReady]);

  return {
    isLoading: isLoadingCountries,
    schema,
    uiSchema,
    countries,
  };
};
